import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, CompassIcon, EyeIcon, HandHeartIcon, MonitorIcon, ShieldCheckIcon, SparklesIcon, UsersIcon, VideoIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Container } from '../components/ui/Container';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { MosqueArchMask } from '../components/ui/MosqueArchMask';
import { PatternDecor } from '../components/ui/PatternDecor';
type Tone = 'sage' | 'sky' | 'rose' | 'sand';
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  sky: 'border-[#C7D4E0]/40 bg-[#EDF2F7]',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const ageGroups = [{
  title: 'من 5 إلى 7 سنوات | الأسس',
  body: 'مدخل لطيف إلى التعلّم والقيم والشعور بالانتماء.',
  points: ['القيم الإسلامية الأساسية من خلال القصص', 'اللطف والاحترام والتعاطف', 'الثقة في التعبير عن الهوية'],
  tone: 'sage' as const
}, {
  title: 'من 8 إلى 10 سنوات | الهوية والثقة',
  body: 'بناء الوعي الذاتي والقوة العاطفية.',
  points: ['مهارات التواصل والصداقات', 'التعامل مع الاختلافات', 'الثقة في المدرسة والمواقف الاجتماعية'],
  tone: 'sky' as const
}, {
  title: 'من 11 إلى 14 سنة | القوة والمسؤولية',
  body: 'إعداد الأطفال لتحديات الحياة الواقعية.',
  points: ['الهوية في بيئة غير مسلمة', 'ضغط الأقران واحترام الذات', 'اتخاذ القرارات الأخلاقية'],
  tone: 'rose' as const
}, {
  title: 'من 15 إلى 18 سنة | القيادة ومهارات الحياة',
  body: 'دعم اليافعين أثناء انتقالهم إلى مرحلة البلوغ بثقة ومسؤولية.',
  points: ['الهدف والمسؤولية', 'أسس احترام الذات', 'التمثيل والقيادة'],
  tone: 'sand' as const
}];
const exploreCards = [{
  icon: SparklesIcon,
  title: 'برامج الأطفال',
  description: 'مسارات تعلّم منظّمة للأطفال المسلمين في البيئات غير المسلمة، تجمع بين الثقة والقيم والانتماء ومهارات الحياة.',
  action: 'استعرض البرامج',
  to: '/ar/children/kids-programs',
  tone: 'sage' as const
}, {
  icon: MonitorIcon,
  title: 'التعلّم عبر الإنترنت',
  description: 'جلسات مباشرة وتفاعلية في مجموعات صغيرة، مصممة للسلامة والمشاركة والتقدم المستمر عبر البلدان والمناطق الزمنية.',
  action: 'كيف يعمل',
  to: '/ar/children/online-learning',
  tone: 'sky' as const
}, {
  icon: CompassIcon,
  title: 'القيم والهوية',
  description: 'هوية قوية دون عزلة: أخلاق، وثقة هادئة، واندماج محترم، وأدوات للتعامل مع الاختلافات.',
  action: 'اعرف المزيد',
  to: '/ar/children/values-identity',
  tone: 'rose' as const
}, {
  icon: HandHeartIcon,
  title: 'المنح الدراسية',
  description: 'منح جزئية وكاملة للأسر التي تحتاج إلى الدعم، تُمنح بشفافية وفق معايير واضحة ومقاعد مدعومة من المانحين.',
  action: 'خيارات الدعم',
  to: '/ar/children/scholarships',
  tone: 'sand' as const
}];
const programModel = [{
  title: 'التعلّم الأساسي',
  details: 'الهوية • الثقة • المهارات الاجتماعية • الأخلاق • الاندماج',
  tone: 'sage' as const
}, {
  title: 'الأساس الإسلامي والثقافي',
  details: 'القيم الإسلامية • أساسيات العربية • القرآن: أساسيات التلاوة والمعاني • الثقافة والتراث',
  tone: 'rose' as const
}, {
  title: 'الإثراء والهوايات',
  details: 'اللغات • الخط • دروس إبداعية • مشاريع',
  tone: 'sand' as const
}];
const onlineFeatures = [{
  icon: UsersIcon,
  title: 'مجموعات صغيرة',
  body: 'من 8 إلى 10 أطفال لمشاركة وتواصل حقيقيين.',
  tone: 'sage' as const
}, {
  icon: VideoIcon,
  title: 'جلسات مباشرة وتفاعلية',
  body: 'ليست وقتًا سلبيًا أمام الشاشة، بل نقاشات وأنشطة موجهة.',
  tone: 'sky' as const
}, {
  icon: ShieldCheckIcon,
  title: 'الإشراف والرفاه',
  body: 'هيكل هادئ يحمي الطفل دون أن يعزله.',
  tone: 'rose' as const
}, {
  icon: EyeIcon,
  title: 'إطلاع الوالدين',
  body: 'إرشادات واضحة وملاحظات منتظمة للوالدين.',
  tone: 'sand' as const
}];
const valuePoints = ['فهم من هم', 'احترام الآخرين مع احترام أنفسهم', 'تنمية شخصية قوية وحسنة الخلق', 'التعامل مع الاختلافات الثقافية بثقة'];
const scholarshipTypes = [{
  title: 'منح جزئية',
  body: 'دعم للأسر ذات الدخل المحدود أو التي تمر بصعوبات مؤقتة.',
  tone: 'sage' as const
}, {
  title: 'منح كاملة',
  body: 'دعم ذو أولوية للأطفال الأكثر احتياجًا، بما في ذلك حالات الإعاقة والأزمات.',
  tone: 'rose' as const
}, {
  title: 'مقاعد برعاية المجتمع',
  body: 'مقاعد ممولة من المانحين والشركاء، تُخصّص وفق معايير وإجراءات إبلاغ واضحة.',
  tone: 'sand' as const
}];
export function ArabicChildren() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-2389" data-mp="2tck56k91ljv9">
      <section className="relative isolate overflow-hidden" data-id="element-2390" data-mp="3ot9lsydyau9">
        <PatternDecor side="left" y="middle" size="lg" data-id="element-2391" data-mp="8o648zvi0m7e" />
        <GeometricAccent variant="dots" className="absolute right-6 top-8 h-32 w-32" opacity={0.4} data-id="element-2392" data-mp="8musacrwg857" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-2393" data-mp="1t8t1uvnjm4he">
          <div className="grid items-center gap-10 lg:grid-cols-12" data-id="element-2394" data-mp="1o8ybqubldixe">
            <div className="lg:col-span-6" data-id="element-2395" data-mp="1jgewlsx9afqh">
              <span className="text-sm font-semibold text-forest-700" data-id="element-2396" data-mp="1jf3uryndfi20">الأطفال دائمًا أولًا</span>
              <h1 className="mt-3 font-display text-5xl font-semibold leading-[1.35] text-ink-900 md:text-7xl" data-id="element-2397" data-mp="1ehan116f6fzm">الأطفال</h1>
              <p className="mt-5 max-w-lg leading-loose text-ink-700" data-id="element-2398" data-mp="1advxnjwlcn9i">
                تدعم برامجنا للأطفال الأطفالَ المسلمين الذين ينشأون في الدول غير المسلمة، وتساعدهم على بناء الثقة والقيم الراسخة والشعور بالانتماء.
              </p>
              <p className="mt-3 max-w-lg leading-loose text-ink-700" data-id="element-2399" data-mp="vjrdzxjyoxd7">
                نجمع بين التعليم والدعم العاطفي وبناء الهوية في بيئة تعلم آمنة ومحترمة.
              </p>
              <div className="mt-7 flex flex-wrap gap-3" data-id="element-2400" data-mp="2pwzadtnl5h0y">
                <Link to="/ar/children/kids-programs" data-id="element-2401" data-mp="2pxin8vydpf84"><Button variant="primary" data-id="element-2402" data-mp="o7tipd4dax5g">استكشف برامج الأطفال</Button></Link>
                <Link to="/ar/children/scholarships" data-id="element-2403" data-mp="o5qjq3l8ltol"><Button variant="secondary" data-id="element-2404" data-mp="sz1sia9m0kj5">المنح الدراسية</Button></Link>
              </div>
            </div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7
          }} className="relative lg:col-span-6" data-id="element-2405" data-mp="eaf1vcej86cm">
              <div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-2406" data-mp="j6dzlqlr30pp">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/nWWygtex9gxYzjLQoiCWG3/children-img-1.jpg" alt="أطفال يتعلمون معًا" data-id="element-2407" data-mp="16whw7e0iu077" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="bg-sage-50/60 py-16 md:py-24" data-id="element-2408" data-mp="11yggn32b0j0h">
        <Container data-id="element-2409" data-mp="1212ol3t3x0j8">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2410" data-mp="3io3u7816pdef">استكشف برامج الأطفال</h2>
          <p className="mt-4 leading-loose text-ink-700" data-id="element-2411" data-mp="3mqi7lcm33kyb">اختر المسار الذي يناسب احتياجات طفلك: البرامج والتعلّم عبر الإنترنت والقيم والمنح الدراسية.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-2412" data-mp="3f7r2n3ae96s9">
            {exploreCards.map((card, index) => <ExploreCard key={card.title} card={card} index={index} data-id="element-2413" data-mp="3a97fizk2doyb" data-mpi={index} />)}
          </div>
        </Container>
      </section>

      <section className="relative overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-2414" data-mp="3k5al6fe4k5q0">
        <GeometricAccent variant="star" className="absolute left-10 top-10 h-32 w-32" opacity={0.18} data-id="element-2415" data-mp="2wfg6dsa8w734" />
        <Container data-id="element-2416" data-mp="2rgdel9j9zxd3">
          <h2 className="max-w-2xl font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2417" data-mp="2rgvfwkksp1rv">نموذج برنامج متوازن</h2>
          <p className="mt-4 leading-loose text-ink-700" data-id="element-2418" data-mp="366sxgho7yt83">تجمع برامجنا للأطفال بين بناء الهوية والأسس الإسلامية ومهارات الحياة، لتساعد الأطفال المسلمين على النمو بثقة في المجتمعات غير المسلمة.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2419" data-mp="2dndyb5bo5z4h">
            {programModel.map((model, index) => <motion.article key={model.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.08
          }} className={`rounded-2xl border p-6 ${toneClasses[model.tone]}`} data-id="element-2420" data-mp="28plwnjl82yn3" data-mpi={index}>
                <h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2421" data-mp="1x3xfnuzaj58o">{model.title}</h3>
                <p className="mt-3 text-sm leading-loose text-ink-700" data-id="element-2422" data-mp="217vee4kpee17">{model.details}</p>
              </motion.article>)}
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-[#EDF2F7]/60 py-16 md:py-24" data-id="element-2423" data-mp="2b3ge1w2av27s">
        <PatternDecor side="right" y="bottom" size="lg" data-id="element-2424" data-mp="2fw9fob2h1ufn" />
        <Container data-id="element-2425" data-mp="2kuba1s5clg6f">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2426" data-mp="2kut20ckz988j">الفئات العمرية والمسارات</h2>
          <p className="mt-4 leading-loose text-ink-700" data-id="element-2427" data-mp="2ox7k2ugto3yr">تتبع كل فئة عمرية منهجًا منظّمًا يتوافق مع احتياجات الأطفال العاطفية والنمائية.</p>
          <p className="mt-3 leading-loose text-ink-700" data-id="element-2428" data-mp="2tsmvzgv9rpsj"><strong className="text-ink-900" data-id="element-2429" data-mp="i8p3lnn6a2y1">ملاحظة حول صيغ المجموعات:</strong> ترحب مسارات الطفولة المبكرة من 5 إلى 10 سنوات بمجموعات مختلطة لتشجيع التعلّم بين الأقران. أما برامج ما قبل المراهقة والمراهقة، من 11 إلى 14 ومن 15 إلى 18 سنة، فتُقدَّم في بيئات مخصّصة حسب الجنس احترامًا للخصوصية والإرشادات الإسلامية للأطفال الأكبر سنًا.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-2430" data-mp="49xu6ej995g8">
            {ageGroups.map((group, index) => <motion.article key={group.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.06
          }} className={`rounded-2xl border p-6 md:p-7 ${toneClasses[group.tone]}`} data-id="element-2431" data-mp="10zyfu4obv5tb" data-mpi={index}>
                <h3 className="font-display text-xl font-semibold leading-loose text-ink-900" data-id="element-2432" data-mp="n6gx2m3gtx4c">{group.title}</h3>
                <p className="mt-2 leading-loose text-ink-700" data-id="element-2433" data-mp="1ko7ft0w44g3d">{group.body}</p>
                <CheckList points={group.points} compact data-id="element-2434" data-mp="1fttg4maf3msw" />
              </motion.article>)}
          </div>
        </Container>
      </section>

      <section className="bg-white/60 py-16 md:py-24" data-id="element-2435" data-mp="hihjyd21jv1o">
        <Container data-id="element-2436" data-mp="hjsqulqs9ix0">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2437" data-mp="vhr0jdu5sr66">تعلّم عبر الإنترنت يشعر الطفل فيه بالأمان</h2>
          <p className="mt-4 leading-loose text-ink-700" data-id="element-2438" data-mp="rfci2uuejtff">السلامة والاحترام والشمول هي جوهر كل جلسة.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-2439" data-mp="15elwpq2ymfha">
            {onlineFeatures.map(feature => {
            const Icon = feature.icon;
            return <article key={feature.title} className={`flex gap-4 rounded-2xl border p-6 ${toneClasses[feature.tone]}`} data-id="element-2440" data-mp="1f5zrxlnvot4m">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-2441" data-mp="1a7y7oxepunrk"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-2442" data-mp="1o4232u1bt25m" /></span>
                  <div data-id="element-2443" data-mp="1j94q5c0xe8ng"><h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2444" data-mp="1j58mgsqqsv18">{feature.title}</h3><p className="mt-1 text-sm leading-loose text-ink-700" data-id="element-2445" data-mp="22r0oa79lb0bw">{feature.body}</p></div>
                </article>;
          })}
          </div>
        </Container>
      </section>

      <section className="bg-sand-50/60 py-16 md:py-24" data-id="element-2446" data-mp="1tuhz964hbwkl">
        <Container data-id="element-2447" data-mp="1xsz7jszmzd3l">
          <div className="grid items-center gap-12 lg:grid-cols-12" data-id="element-2448" data-mp="1xsfp8b62qmp1">
            <div className="lg:col-span-6" data-id="element-2449" data-mp="1k2c5prykx73a">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2450" data-mp="1k0izgdrvoovt">القيم والهوية</h2>
              <p className="mt-4 leading-loose text-ink-700" data-id="element-2451" data-mp="1a4g0thw8j8dm">ينبغي أن تكون الهوية الإسلامية مصدر قوة، لا مصدر خوف أو عزلة أو ضغط.</p>
              <div className="mt-6 rounded-2xl border border-sage-200 bg-sage-50 p-6" data-id="element-2452" data-mp="1f4kgx6t8e13b"><CheckList points={valuePoints} data-id="element-2453" data-mp="113zy4anbg59t" /></div>
            </div>
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} className="relative lg:col-span-6" data-id="element-2454" data-mp="115ui9mqrjtcu">
              <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-card" data-id="element-2455" data-mp="37u36ezbwbb4p"><img src="https://cdn.magicpatterns.com/uploads/5AD965Z9n7QyUcMmg3hGiN/children-img-2.jpg" alt="أطفال يحملون بطاقات تعليمية" className="h-full w-full object-cover" data-id="element-2456" data-mp="330ib5f1osv97" /></div>
              <GeometricAccent variant="star" className="absolute -bottom-6 -right-6 h-32 w-32" opacity={0.18} data-id="element-2457" data-mp="2u1n0nqrm0z03" />
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-[#F7EDE9]/60 py-16 md:py-24" data-id="element-2458" data-mp="3lrjfyjphnl0o">
        <PatternDecor side="left" y="top" size="md" data-id="element-2459" data-mp="3hpoenf7k57mw" />
        <Container data-id="element-2460" data-mp="3hsaoxmlu218t">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2461" data-mp="3cr3effsb2nyg">إتاحة الفرصة لكل طفل</h2>
          <p className="mt-4 leading-loose text-ink-700" data-id="element-2462" data-mp="26bawmqzhjmes">لا ينبغي أن يُحرم أي طفل من الدعم بسبب الظروف المالية. تُدعم المنح الدراسية من خلال التبرعات والشراكات المؤسسية وتُمنح بشفافية.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2463" data-mp="gufe116kbgpp">
            {scholarshipTypes.map(scholarship => <article key={scholarship.title} className={`rounded-2xl border p-6 ${toneClasses[scholarship.tone]}`} data-id="element-2464" data-mp="lsgv5gkfh5yj"><h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2465" data-mp="pu22yni3uu1v">{scholarship.title}</h3><p className="mt-2 text-sm leading-loose text-ink-700" data-id="element-2466" data-mp="ultyzdk15rze">{scholarship.body}</p></article>)}
          </div>
        </Container>
      </section>

      <section className="bg-white/60 py-16 md:py-24" data-id="element-2467" data-mp="zk53d8rk3aa8">
        <Container data-id="element-2468" data-mp="14iotlld6n88e">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-2469" data-mp="14i6saabny3tm">
            <div className="max-w-xl" data-id="element-2470" data-mp="19jw3bpyvyhhp"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2471" data-mp="19jcmkckz1z59">هل أنتم مستعدون للبدء؟</h2><p className="mt-4 leading-loose text-ink-700" data-id="element-2472" data-mp="1dhtrqqku0zk1">اختر برنامجًا يناسب عمر طفلك واحتياجاته، وابدأ ببناء الثقة والقيم والانتماء في مساحة تعليمية آمنة.</p></div>
            <div className="flex flex-wrap gap-3" data-id="element-2473" data-mp="3fc1dyyaioc5a"><Link to="/ar/children/kids-programs" data-id="element-2474" data-mp="2k1cq9608wpia"><Button variant="primary" data-id="element-2475" data-mp="2k0bdmx3kzuyg">عرض برامج الأطفال</Button></Link><Link to="/ar/children/scholarships" data-id="element-2476" data-mp="2fzrhnwuquxdw"><Button variant="secondary" data-id="element-2477" data-mp="2g0j0s875n2na">التقدّم بطلب منحة</Button></Link></div>
          </div>
        </Container>
      </section>
    </div>;
}
function ExploreCard({
  card,
  index
}: {
  card: (typeof exploreCards)[number];
  index: number;
}) {
  const Icon = card.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} whileHover={{
    y: -2
  }} data-id="element-2478" data-mp="21alj8b3qdb72">
      <Link to={card.to} className={`group flex h-full flex-col gap-4 rounded-2xl border p-6 transition-shadow hover:shadow-card md:p-7 ${toneClasses[card.tone]}`} data-id="element-2479" data-mp="2b0emmotpyk08">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-forest-700/15 bg-white text-forest-700" data-id="element-2480" data-mp="267v89pn7jkud"><Icon size={20} strokeWidth={1.5} aria-hidden="true" data-id="element-2481" data-mp="1dlka0ln2pw0q" /></span>
        <h3 className="font-display text-xl font-semibold leading-loose text-ink-900" data-id="element-2482" data-mp="1djg5q2l9iuzx">{card.title}</h3>
        <p className="text-[15px] leading-loose text-ink-700" data-id="element-2483" data-mp="3p6t1w2hl24gj">{card.description}</p>
        <span className="mt-auto text-sm font-medium text-forest-700" data-id="element-2484" data-mp="3u0nnjbgr0ti9">{card.action} <span aria-hidden="true" data-id="element-2485" data-mp="3g98yoo05ty3a">←</span></span>
      </Link>
    </motion.div>;
}
function CheckList({
  points,
  compact = false
}: {
  points: string[];
  compact?: boolean;
}) {
  return <ul className={compact ? 'mt-4 space-y-2' : 'space-y-3'} data-id="element-2486" data-mp="busr4s47x5xd">
      {points.map(point => <li key={point} className="flex items-center gap-3 leading-loose text-ink-700" data-id="element-2487" data-mp="gtcb4mz947n3"><CheckCircle2Icon size={compact ? 14 : 16} className="shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-2488" data-mp="2t1ltodlb141" /><span data-id="element-2489" data-mp="7rml9ll0pgrf">{point}</span></li>)}
    </ul>;
}